import {
  useGetQuestionnaireData,
  useGetQuestionnaireDocument,
} from "@/services/api/questionnaireService"
import { Questionnaire } from "@/types/questionnaire/models"
import { UseQueryResult } from "@tanstack/react-query"
import {
  ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  useOnboardingDemoQuestionnaire,
} from "../demoData"

export type QuestionnaireDataQuery = UseQueryResult<Questionnaire> & {
  isDemo: boolean
}

type UseOnboardingQuestionnaireDataOptions = {
  demoSourcePath?: string
}

export const useOnboardingQuestionnaireData = (
  questionnaireId?: string,
  options?: UseOnboardingQuestionnaireDataOptions,
): QuestionnaireDataQuery => {
  const isDemo = questionnaireId === ONBOARDING_DEMO_QUESTIONNAIRE_ID
  const demoQuery = useOnboardingDemoQuestionnaire(isDemo, {
    sourcePath: options?.demoSourcePath,
  })
  const apiQuery = useGetQuestionnaireData(questionnaireId, {
    enabled: !isDemo && !!questionnaireId,
  })

  return (
    isDemo ? { ...demoQuery, isDemo: true } : { ...apiQuery, isDemo: false }
  ) as QuestionnaireDataQuery
}

type UseOnboardingQuestionnaireDocumentOptions = {
  demoDocumentVariant?: "original" | "answered"
}

export function useOnboardingQuestionnaireDocument(
  questionnaireId?: string,
  shouldFetch: boolean = true,
  options?: UseOnboardingQuestionnaireDocumentOptions,
) {
  const isDemo = questionnaireId === ONBOARDING_DEMO_QUESTIONNAIRE_ID

  // For demo questionnaires, still use the backend API - it will serve from public URLs
  const apiQuery = useGetQuestionnaireDocument(
    questionnaireId,
    shouldFetch && !!questionnaireId,
    {
      demoVariant: isDemo ? options?.demoDocumentVariant : undefined,
    },
  )

  return { ...apiQuery, isDemo }
}
